import { B2bPartnershipType } from 'common/B2BVerification/constants'
import { sessionStorageKeys } from 'constants/common'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'

export const formatAndValidateB2bDetails = (
  b2bPartnerDetails: BusinessPartner,
  partnershipCodeFromQuery: string,
  isPatient: boolean
): B2BDetails => {
  b2bPartnerDetails.partnershipCode = partnershipCodeFromQuery
  const { id, dealStructure } = b2bPartnerDetails ?? {}
  const isGoldPartnerClient =
    isPatient && dealStructure === B2bPartnershipType.NON_CONFORMING

  /*
    Return undefined to reset b2b details state if the partnership is not valid
    or the partner type is fully-covered or non-conforming for client user
  */
  if (!id || isGoldPartnerClient) {
    return undefined
  }

  const formattedB2bDetails = convertToB2bDetails(b2bPartnerDetails)
  return formattedB2bDetails
}

export const convertToB2bDetails = (
  b2bPartnerDetails: BusinessPartner
): B2BDetails => {
  const {
    id,
    name,
    partnershipCode,
    hidePaymentInput,
    hideInsuranceInput,
    hidePostBookingModalCopy,
    customSuccessMessage,
    customFailureMessage,
    authorizationRequired,
    channelType,
    dealStructure,
    isCreditCardOptional,
    priorAuthConfiguration,
  } = b2bPartnerDetails ?? {}
  return {
    partnerId: id,
    partnerName: name,
    isB2BUser: true,
    keyword: partnershipCode,
    flags: {
      hideInsurance: hideInsuranceInput,
      hidePayment: hidePaymentInput,
      hidePostBookingModalCopy: hidePostBookingModalCopy,
      isCreditCardOptional,
    },
    verificationMessages: {
      customSuccessMessage: customSuccessMessage,
      customFailureMessage: customFailureMessage,
    },
    authorizationRequired,
    ...(channelType && { channelType }),
    ...(dealStructure && { dealStructure }),
    ...(priorAuthConfiguration && { priorAuthConfiguration }),
  }
}

export const getStoredPartnershipCode = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  return sessionStorage.getItem(sessionStorageKeys.PARTNERSHIP_CODE) || null
}

export const getPartnershipCodeFromSearchQuery = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const providerSearchQuery = JSON.parse(
      sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
    )

    return providerSearchQuery?.partnershipCode || null
  } catch {
    return null
  }
}

export const setStoredPartnershipCode = (code: string): void => {
  sessionStorage.setItem(sessionStorageKeys.PARTNERSHIP_CODE, code || '')
}

export const clearStoredPartnershipCode = (): void => {
  sessionStorage.removeItem(sessionStorageKeys.PARTNERSHIP_CODE)
}

export const addKeywordToStoredSearchQuery = (
  partnershipCode: string
): void => {
  setStoredPartnershipCode(partnershipCode)
  const storedProviderSearchQuery = JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
  )
  sessionStorage.setItem(
    sessionStorageKeys.PROVIDER_SEARCH_QUERY,
    JSON.stringify({
      ...storedProviderSearchQuery,
      partnershipCode,
    })
  )
}

export const extractKeywordFromQuery = (query: ParsedUrlQuery): string => {
  const { redirectTo, partnershipCode } = query ?? {}
  let b2bPartnershipCode

  if (!!redirectTo) {
    const redirectToQuery = (redirectTo as string).split('?')[1]
    const redirectUrlQueryParams = qs.parse(redirectToQuery)
    b2bPartnershipCode = redirectUrlQueryParams?.partnershipCode
  } else {
    b2bPartnershipCode = partnershipCode
  }

  return b2bPartnershipCode
}

export const normalizePriorAuthConfiguration = (
  priorAuthConfiguration: PriorAuthConfiguration
): PriorAuthConfiguration => {
  const {
    priorAuthOptional = false,
    showSessionLimitCapture = false,
    showReferralReason = false,
    showReferredBy = false,
    showSubscriberIdInput = true,
    subscriberIdInputOptional = false,
    defaultSubscriberIdInput = '',
    subscriberInputLabel,
  } = priorAuthConfiguration ?? {}

  return {
    priorAuthOptional,
    showSessionLimitCapture,
    showReferralReason,
    showReferredBy,
    showSubscriberIdInput,
    subscriberIdInputOptional,
    defaultSubscriberIdInput,
    subscriberInputLabel:
      subscriberInputLabel?.trim() || 'Member/Subscriber ID',
  }
}
